/**
 * Onboarding wizard shell.
 *
 * - Mounted at app root (so it works regardless of which layout — sidebar,
 *   cashier, or correspondent — the user is on).
 * - Loads /api/me/onboarding once, decides whether to show.
 * - Renders the role-specific wizard component as a fullscreen overlay.
 * - All step submissions go through standard create endpoints; the wizard
 *   only PUTs to /api/me/onboarding to advance the step counter.
 */
import { useEffect, useState, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import EntityAdminWizard from './EntityAdminWizard';
import CashierWizard from './CashierWizard';
import CorrespondentWizard from './CorrespondentWizard';

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const OnboardingCtx = createContext(null);
export const useOnboarding = () => useContext(OnboardingCtx);

// Feature flag — keep all wizard code in place but disabled in UI for now.
// Re-enable by flipping this to true (or wiring to env later).
const ONBOARDING_ENABLED = false;

export default function OnboardingProvider({ children }) {
  const { user, isAuthenticated, mustChangePassword } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const reload = useCallback(async () => {
    if (!ONBOARDING_ENABLED) return;
    if (!isAuthenticated || mustChangePassword) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/me/onboarding`, { headers: authHeaders() });
      setState(res.data);
      // Auto-show if not yet completed AND a flavor exists.
      if (res.data && res.data.flavor && !res.data.completed) {
        setVisible(true);
      }
    } catch (e) { /* silent — wizard is non-critical */ }
    finally { setLoading(false); }
  }, [isAuthenticated, mustChangePassword]);

  useEffect(() => { reload(); }, [reload]);

  const setStep = useCallback(async (step) => {
    try {
      const res = await axios.put(`${API_URL}/api/me/onboarding`, { step }, { headers: authHeaders() });
      setState(res.data);
    } catch (e) { /* silent */ }
  }, []);

  const skipStep = useCallback(async (step) => {
    const skipped = [...(state?.skipped_steps || []), step];
    try {
      const res = await axios.put(
        `${API_URL}/api/me/onboarding`,
        { step: step + 1, skipped_steps: skipped },
        { headers: authHeaders() }
      );
      setState(res.data);
    } catch (e) { /* silent */ }
  }, [state]);

  const complete = useCallback(async () => {
    try {
      const res = await axios.put(
        `${API_URL}/api/me/onboarding`,
        { completed: true },
        { headers: authHeaders() }
      );
      setState(res.data);
    } catch (e) { /* silent */ }
    setVisible(false);
  }, []);

  const launch = useCallback(async () => {
    // "Setup Guide" link from avatar menu — reset progress & show
    try {
      const res = await axios.post(`${API_URL}/api/me/onboarding/reset`, {}, { headers: authHeaders() });
      setState(res.data);
      setVisible(true);
    } catch (e) { /* silent */ }
  }, []);

  const dismiss = useCallback(() => setVisible(false), []);

  const ctx = {
    state,
    loading,
    visible,
    setStep,
    skipStep,
    complete,
    launch,
    dismiss,
    canShowSetupGuide: ONBOARDING_ENABLED && !!(user && ['entity_admin', 'campus_agent', 'campus_correspondent'].includes(user.role)),
  };

  return (
    <OnboardingCtx.Provider value={ctx}>
      {children}
      {visible && state?.flavor === 'entity_admin' && (
        <EntityAdminWizard
          state={state}
          onAdvance={setStep}
          onSkip={skipStep}
          onComplete={complete}
          onDismiss={dismiss}
        />
      )}
      {visible && state?.flavor === 'campus_agent' && (
        <CashierWizard
          state={state}
          onAdvance={setStep}
          onSkip={skipStep}
          onComplete={complete}
          onDismiss={dismiss}
        />
      )}
      {visible && state?.flavor === 'campus_correspondent' && (
        <CorrespondentWizard
          state={state}
          onAdvance={setStep}
          onSkip={skipStep}
          onComplete={complete}
          onDismiss={dismiss}
        />
      )}
    </OnboardingCtx.Provider>
  );
}
